import { storeToRefs } from 'pinia'
import { useContactStore as useStore } from '@/stores/ContactStore'
import { FetchContactsSearchParams, Contact } from '@/model/types'

export default function useContactStore() {
    const contactStore = useStore()
    const { contacts, loading, selectedContact, contactsLinks } = storeToRefs(contactStore)

    const handleError = (error: any, message: string) => {
        console.error(message, error)
        // Optionally, implement additional user-facing error feedback here
    }

    // Fetches contacts with specified search parameters
    const fetchContacts = async (params: FetchContactsSearchParams) => {
        try {
            await contactStore.fetchContacts(params)
        } catch (error) {
            handleError(error, 'Failed to fetch contacts')
        } finally {
        }
    }

    // Fetches contacts based on a search query
    const fetchContactsSearch = async (params: FetchContactsSearchParams) => {
        try {
            await contactStore.fetchContactsSearch(params)
        } catch (error) {
            handleError(error, 'Failed to fetch contacts search')
        } finally {
        }
    }

    // Fetches a limited list of contacts with pagination
    const fetchContactsLimit = async (params: FetchContactsSearchParams & { done: Function }) => {
        try {
            await contactStore.fetchContactsLimit(params)
        } catch (error) {
            handleError(error, 'Failed to fetch contacts limit')
        } finally {
        }
    }

    // Creates a new contact
    const createContact = async (contact: Contact) => {
        try {
            await contactStore.createContact({ contact })
        } catch (error) {
            handleError(error, 'Failed to create contact')
        }
    }

    // Updates an existing contact
    const updateContact = async (contactId: number, contact: Contact) => {
        try {
            await contactStore.updateContact({ contactId, contact })
        } catch (error) {
            handleError(error, 'Failed to update contact')
        }
    }

    // Deletes a contact
    const deleteContact = async (contactId: number) => {
        try {
            await contactStore.deleteContact({ contactId })
        } catch (error) {
            handleError(error, 'Failed to delete contact')
        }
    }

    // Duplicates a contact
    const duplicateContact = async (contact: Contact) => {
        try {
            await contactStore.duplicateContact({ contact })
        } catch (error) {
            handleError(error, 'Failed to duplicate contact')
        }
    }

    return {
        contacts,
        loading,
        selectedContact,
        contactsLinks,
        fetchContacts,
        fetchContactsSearch,
        fetchContactsLimit,
        createContact,
        updateContact,
        deleteContact,
        duplicateContact,
    }
}
